import { useMutation, useQueryClient } from '@tanstack/react-query';
import { uiPreferenceRequests, type UiPreferences } from '@/lib/api/requests/ui-preferences';
import { uiPreferenceKeys } from './keys';

/**
 * Saves part of the preferences. The cache is updated first so a toggle
 * answers at once, and put back if the server refuses.
 */
export function useUpdateUiPreferences() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (patch: Partial<UiPreferences>) => uiPreferenceRequests.update(patch),
    onMutate: async (patch) => {
      await queryClient.cancelQueries({ queryKey: uiPreferenceKeys.all });
      const previous = queryClient.getQueryData<UiPreferences>(uiPreferenceKeys.all);
      queryClient.setQueryData<UiPreferences>(uiPreferenceKeys.all, (current) => ({
        ...current,
        ...patch,
      }));
      return { previous };
    },
    onError: (_error, _patch, context) => {
      if (context?.previous) {
        queryClient.setQueryData(uiPreferenceKeys.all, context.previous);
      } else {
        // Nothing was cached to go back to, so ask the server what is stored.
        void queryClient.invalidateQueries({ queryKey: uiPreferenceKeys.all });
      }
    },
    onSuccess: (saved) => {
      queryClient.setQueryData(uiPreferenceKeys.all, saved);
    },
  });
}
